import { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import Tooltip from 'components/shared/Tooltip';
import { colors } from 'styles/theme/colors';

type Tone = 'green' | 'red' | 'default';

type StatTileProps = {
  label: string;
  primary: ReactNode;
  addendum?: ReactNode;
  tone?: Tone;
  size?: 'large' | 'medium';
  loading?: boolean;
};

// Vertical stack: label, big primary value, optional small grey addendum underneath.
// Used for every metric panel in the slideout headers (rate tiles, top-by tiles, summary tiles).
export function StatTile({
  label,
  primary,
  addendum,
  tone = 'default',
  size = 'large',
  loading = false,
}: StatTileProps) {
  // A colored em-dash reads as broken; fall back to default tone whenever the value is empty.
  const effectiveTone: Tone = loading || primary == null || primary === '\u2014' ? 'default' : tone;
  return (
    <Tooltip text={primary} disabled={loading} position='top'>
      <StatBox>
        <StatLabel>{label}</StatLabel>
        {loading ? (
          <SpinnerRow>
            <CircularProgress color={colors.primaryBlue} size={20} thickness={2} />
          </SpinnerRow>
        ) : (
          <>
            <PrimaryValue $tone={effectiveTone} $size={size}>
              {primary}
            </PrimaryValue>
            {addendum != null && <Addendum>{addendum}</Addendum>}
          </>
        )}
      </StatBox>
    </Tooltip>
  );
}

const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Allow children to shrink below their content width so ellipsis truncation actually kicks in
  // when the tile is in a grid column narrower than the value/addendum text.
  min-width: 0;
`;

export const TilesGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, 1fr);
  gap: 12px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 8px;

    ${StatBox} {
      background-color: #f2f3f4;
      border: none;
    }

    ${({ $columns }) =>
      $columns === 4 &&
      css`
        grid-template-columns: 1fr 1fr;
        gap: 8px 4px;
      `}
  }
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;
`;

const PrimaryValue = styled.span<{ $tone: Tone; $size: 'large' | 'medium' }>`
  font-size: ${({ $size }) => ($size === 'medium' ? '16px' : '20px')};
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'green') return theme.colors.accentGreen;
    if ($tone === 'red') return theme.colors.accentRed;
    return theme.colors.black;
  }};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpinnerRow = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
`;

const Addendum = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
